// Auth utility functions

export const getAuthToken = () => {
  return localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
};

export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem('auth_token', token);
  } else {
    localStorage.removeItem('auth_token');
  }
};

export const removeAuthToken = () => {
  localStorage.removeItem('auth_token');
  sessionStorage.removeItem('auth_token');
};

export const isAuthenticated = () => {
  const token = getAuthToken();
  return !!token;
};

export const logout = () => {
  hardClearAuthStorage();
  window.location.replace('/login');
};

/**
 * Wipe every auth key (redux-persist uses persist:root, not persist:auth).
 * Keep theme/language prefs. Then hard-navigate so PersistGate cannot
 * rehydrate the old session and bounce the user to /home.
 */
export const hardClearAuthStorage = () => {
  const preserve = {};
  ['color_theme', 'i18nextLng', 'age_gate_accepted_v1'].forEach((k) => {
    try {
      const v = localStorage.getItem(k);
      if (v !== null) preserve[k] = v;
    } catch (_) {}
  });
  try { localStorage.clear(); } catch (_) {}
  Object.entries(preserve).forEach(([k, v]) => {
    try { localStorage.setItem(k, v); } catch (_) {}
  });
  try { sessionStorage.clear(); } catch (_) {}
};

export const hardLogoutRedirect = (path = '/login') => {
  hardClearAuthStorage();
  window.location.replace(path);
};

// ─── Multi-Account Management ────────────────────────────────
// Stores array of { id, name, username, avatar, token } in localStorage

const SAVED_ACCOUNTS_KEY = 'saved_accounts';
const ACTING_AS_KEY = 'acting_as_creator';
const ORIGINAL_TOKEN_KEY = 'original_user_token';
const ORIGINAL_USER_KEY = 'original_user_data';

/**
 * Get all saved accounts (for multi-account switching).
 */
export const getSavedAccounts = () => {
  try {
    const raw = localStorage.getItem(SAVED_ACCOUNTS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

/**
 * Save/update an account in the saved accounts list.
 */
export const saveAccount = (account) => {
  const accounts = getSavedAccounts();
  const idx = accounts.findIndex(a => a.id === account.id);
  const entry = {
    id: account.id,
    name: account.name,
    username: account.username,
    avatar: account.avatar,
    token: account.token,
    user_type: account.user_type,
  };
  if (idx >= 0) {
    accounts[idx] = entry;
  } else {
    accounts.push(entry);
  }
  localStorage.setItem(SAVED_ACCOUNTS_KEY, JSON.stringify(accounts));
};

/**
 * Remove a saved account.
 */
export const removeSavedAccount = (userId) => {
  const accounts = getSavedAccounts().filter(a => a.id !== userId);
  localStorage.setItem(SAVED_ACCOUNTS_KEY, JSON.stringify(accounts));
};

/**
 * Save original user context before switching to a creator account.
 */
export const saveOriginalUser = (user, token) => {
  localStorage.setItem(ORIGINAL_TOKEN_KEY, token);
  localStorage.setItem(ORIGINAL_USER_KEY, JSON.stringify({
    id: user.id,
    name: user.display_name || user.username,
    username: user.username,
    avatar: user.avatar,
    user_type: user.user_type,
  }));
};

/**
 * Get the original user (before delegate switch).
 */
export const getOriginalUser = () => {
  try {
    const raw = localStorage.getItem(ORIGINAL_USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

/**
 * Get the original user's token.
 */
export const getOriginalToken = () => {
  return localStorage.getItem(ORIGINAL_TOKEN_KEY);
};

/**
 * Clear delegate access context (when switching back).
 */
export const clearActingAs = () => {
  localStorage.removeItem(ACTING_AS_KEY);
  localStorage.removeItem(ORIGINAL_TOKEN_KEY);
  localStorage.removeItem(ORIGINAL_USER_KEY);
};

/**
 * Set acting-as context (which creator we're acting as).
 */
export const setActingAs = (creatorData) => {
  localStorage.setItem(ACTING_AS_KEY, JSON.stringify(creatorData));
};

/**
 * Get current acting-as context.
 */
export const getActingAs = () => {
  try {
    const raw = localStorage.getItem(ACTING_AS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

/**
 * Check if currently acting as another account.
 */
export const isActingAs = () => {
  return !!localStorage.getItem(ACTING_AS_KEY);
};